import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, BadgeCheck } from "lucide-react";

// ✅ Replace these with your real badge imports (svg/png)
// Example:
// import gmp from "../assets/badges/gmp.png";
import gmp from "../assets/badges/gmp.png";
import haccp from "../assets/badges/haccp.png";
import fda from "../assets/badges/fda.jpg";
import fssai from "../assets/badges/fssai.png";
import ayush from "../assets/badges/ayush.png";
import vegan from "../assets/badges/vegan.jpg";
import usda1 from "../assets/badges/usda1.png";
import usda2 from "../assets/badges/usda2.png";
import organicLeaf from "../assets/badges/organic-leaf.jpg";

type Badge = {
  id: string;
  label: string;
  src: string;
  // optional bigger badges like FSSAI leaf etc
  size?: "sm" | "md" | "lg";
};

const wrap = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const item = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45 } },
};

export default function CertificationBadges() {
  const badges: Badge[] = useMemo(
    () => [
      { id: "gmp", label: "GMP Certified", src: gmp, size: "md" },
      { id: "haccp", label: "HACCP", src: haccp, size: "md" },
      { id: "fda", label: "FDA", src: fda, size: "md" },
      { id: "fssai", label: "FSSAI", src: fssai, size: "lg" },
      { id: "ayush", label: "AYUSH", src: ayush, size: "md" },

      { id: "vegan", label: "Vegan Product", src: vegan, size: "lg" },
      { id: "usda1", label: "USDA Organic", src: usda1, size: "sm" },
      { id: "usda2", label: "USDA Organic", src: usda2, size: "sm" },
      { id: "leaf", label: "Organic", src: organicLeaf, size: "lg" },
    ],
    []
  );

  const [expanded, setExpanded] = useState(false);
  const initialCount = 5; // top row like your screenshot

  const visibleBadges = expanded ? badges : badges.slice(0, initialCount);

  return (
    <section className="relative overflow-hidden py-14 md:py-20 bg-[#F6F7F4]">
      {/* Premium background texture (subtle) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_18%_18%,rgba(244,124,32,0.10),transparent_60%),radial-gradient(900px_circle_at_82%_22%,rgba(232,62,111,0.08),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_45%,rgba(0,0,0,0.035)_100%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
        {/* Header */}
        <motion.div
          variants={wrap}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 backdrop-blur">
            <BadgeCheck size={16} className="text-[#C62828]" />
            <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#2B2B2B]/75">
              Certifications & Compliance
            </span>
          </div>

          <h2 className="mt-5 text-3xl md:text-4xl font-extrabold text-[#1B1B1B]">
            Globally Recognized{" "}
            <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
              Standards
            </span>
          </h2>

          <p className="mt-3 text-base md:text-lg text-[#2B2B2B]/75 leading-relaxed">
            We follow stringent quality systems and certifications to ensure export-ready,
            compliant manufacturing across international markets.
          </p>
        </motion.div>

        {/* Badges */}
        <motion.div
          variants={wrap}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-10"
        >
          <AnimatePresence mode="popLayout">
            <motion.div
              layout
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-10 gap-y-10 items-center justify-items-center"
            >
              {visibleBadges.map((b) => (
                <motion.div
                  key={b.id}
                  variants={item}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  whileHover={{ y: -6, rotate: -0.5, scale: 1.04 }}
                  transition={{ type: "spring", stiffness: 280, damping: 22 }}
                  className="group relative"
                >
                  {/* subtle hover halo */}
                  <div className="pointer-events-none absolute -inset-6 rounded-full opacity-0 group-hover:opacity-100 transition duration-300 blur-2xl bg-gradient-to-r from-[#F47C20]/15 to-[#E83E6F]/15" />

                  <div
                    className={[
                      "relative flex items-center justify-center",
                      b.size === "sm"
                        ? "h-14 w-14"
                        : b.size === "lg"
                        ? "h-24 w-24 md:h-28 md:w-28"
                        : "h-20 w-20 md:h-24 md:w-24",
                    ].join(" ")}
                  >
                    <img
                      src={b.src}
                      alt={b.label}
                      draggable={false}
                      className="max-h-full max-w-full object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.10)]"
                    />
                  </div>

                  {/* label (optional, only on hover for premium look) */}
                  <div className="mt-3 text-center text-xs font-medium text-[#2B2B2B]/60 opacity-0 group-hover:opacity-100 transition">
                    {b.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Button */}
          <div className="mt-10 flex justify-center">
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white
                         bg-gradient-to-r from-[#C62828] to-[#E83E6F]
                         shadow-lg shadow-black/15 hover:opacity-95 transition"
            >
              {expanded ? "See Less" : "See More"}
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}